// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable guard-for-in */

import EventHandler from '../../utils/dom/event-handler';
import SelectorEngine from '../../utils/dom/selector-engine';
import Request, { ApiMode } from '../../utils/request';
import Modal from '../../components/Modal';
import FormGenerator, { FormTypes } from '../../components/FormGenerator';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';
import Manipulator from '../../utils/dom/manipulator';
import dataLayer from '../../utils/data-layer';

// class names
const CLASS_WRAPPER = 'p-activators-portal-onboarding';
const CLASS_SPLASH = 'p-activators-portal-onboarding-splash-bg';
const CLASS_SPLASH_NEXT = 'p-activators-portal-onboarding-splash-next';

const CLASS_SUCCESS = 'p-activators-portal-onboarding-success';
const CLASS_SUCCESS_BUTTON = 'p-activators-portal-onboarding-success--button';

const CLASS_STEP = 'p-activators-portal-onboarding-step';
const CLASS_QUESTION = 'p-activators-portal-onboarding-questions';
const CLASS_STEP_NEXT = 'p-activators-portal-onboarding-step-next';

const CLASS_EXIT_BUTTON = 'js-exit-activators-portal-onboarding';
const CLASS_EXIT_CONFIRM_MODAL = 'js-exit-activators-portal-onboarding-confirm';
const CLASS_EXIT_CONFIRM_YES = 'js-exit-activators-portal-onboarding-confirm-yes';
const CLASS_EXIT_CONFIRM_NO = 'js-exit-activators-portal-onboarding-confirm-no';

const CLASS_ERROR_CONTAINER = 'p-activators-portal-onboarding-error-container';
const CLASS_ERROR_TEXT = 'error-container-message-text';
const CLASS_DISMISS_ERROR = 'js-dismiss-error-button';

// selectors
const SELECTOR_WRAPPER = `.${CLASS_WRAPPER}`;
const SELECTOR_MODAL = '#js-activators-portal-onboarding-modal';

const SELECTOR_SPLASH = `.${CLASS_SPLASH}`;
const SELECTOR_SPLASH_NEXT = `.${CLASS_SPLASH_NEXT}`;

const SELECTOR_SUCCESS = `.${CLASS_SUCCESS}`;
const SELECTOR_SUCCESS_BUTTON = `.${CLASS_SUCCESS_BUTTON}`;

const SELECTOR_STEP = `.${CLASS_STEP}`;
const SELECTOR_QUESTION = `.${CLASS_QUESTION}`;
const SELECTOR_STEP_NEXT = `.${CLASS_STEP_NEXT}`;

const SELECTOR_EXIT_BUTTON = `.${CLASS_EXIT_BUTTON}`;
const SELECTOR_EXIT_CONFIRM_MODAL = `.${CLASS_EXIT_CONFIRM_MODAL}`;
const SELECTOR_EXIT_CONFIRM_YES = `.${CLASS_EXIT_CONFIRM_YES}`;
const SELECTOR_EXIT_CONFIRM_NO = `.${CLASS_EXIT_CONFIRM_NO}`;

const SELECTOR_ERROR_CONTAINER = `.${CLASS_ERROR_CONTAINER}`;
const SELECTOR_ERROR_TEXT = `.${CLASS_ERROR_TEXT}`;
const SELECTOR_DISMISS_ERROR = `.${CLASS_DISMISS_ERROR}`;

const KEY_OBJECT = 'fieldName';
const VALUE_OBJECT = 'value';
const REFERENCE_VALUE_OBJECT = 'label';

const CLICK_EVENT = 'click';
const HIDDEN_CLASS = 'u-hidden';

const IS_LOCAL = false;

class ActivatorsPortalOnboarding {
	constructor() {
		this._wrapper = SelectorEngine.findOne(SELECTOR_WRAPPER);
		this.formElementsList = {};
		if (!this._wrapper) {
			return;
		}
		this.init();
	}

	get stepsData() {
		return this._stepsData;
	}

	set stepsData(_stepsData) {
		this._stepsData = _stepsData;
		if (_stepsData?.length > 0) {
			this.initiateFormGenerator(_stepsData);
		}
	}

	init() {
		this.ActivatorsPortalOnboardingModalElement = SelectorEngine.findOne(SELECTOR_MODAL, this._wrapper);

		this.ActivatorsPortalOnboardingSplash = SelectorEngine.findOne(SELECTOR_SPLASH, this._wrapper);
		this.ActivatorsPortalOnboardingSplashNext = SelectorEngine.findOne(
			SELECTOR_SPLASH_NEXT,
			this.ActivatorsPortalOnboardingSplash
		);

		this.ActivatorsPortalOnboardingStep = SelectorEngine.findOne(SELECTOR_STEP, this._wrapper);
		this.ActivatorsPortalOnboardingQuestion = SelectorEngine.findOne(
			SELECTOR_QUESTION,
			this.ActivatorsPortalOnboardingStep
		);
		this.ActivatorsPortalOnboardingStepNext = SelectorEngine.findOne(
			SELECTOR_STEP_NEXT,
			this.ActivatorsPortalOnboardingStep
		);

		this.ActivatorsPortalOnboardingSuccess = SelectorEngine.findOne(SELECTOR_SUCCESS, this._wrapper);
		this.ActivatorsPortalOnboardingSuccessButton = SelectorEngine.findOne(
			SELECTOR_SUCCESS_BUTTON,
			this.ActivatorsPortalOnboardingSuccess
		);

		this.exitButton = SelectorEngine.findOne(SELECTOR_EXIT_BUTTON, this._wrapper);
		const exitConfirmModalHtml = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_MODAL, this._wrapper);
		this.exitConfirmModal = new Modal(exitConfirmModalHtml, { backdrop: 'static' });

		this.exitConfirmYes = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_YES, this._wrapper);
		this.exitConfirmNo = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_NO, this._wrapper);

		this.errorContainer = SelectorEngine.findOne(SELECTOR_ERROR_CONTAINER, this._wrapper);
		this.errorText = SelectorEngine.findOne(SELECTOR_ERROR_TEXT, this.errorContainer);
		this.dismissErrorButton = SelectorEngine.findOne(SELECTOR_DISMISS_ERROR, this.errorContainer);

		this.formSteps = Manipulator.getDataAttribute(this._wrapper, 'form-steps-api');
		this.updateRole = Manipulator.getDataAttribute(this._wrapper, 'update-role-api');
		this.userRole = Manipulator.getDataAttribute(this._wrapper, 'primary-role');
		this.saveActivatorOnboardingDetails = Manipulator.getDataAttribute(this._wrapper, 'submit-onboarding-activator');

		this.ActivatorsPortalOnboardingModal = new Modal(this.ActivatorsPortalOnboardingModalElement, {
			backdrop: 'static'
		});
		this.ActivatorsPortalOnboardingModal.isEscapeEventEnable = false;

		this.ActivatorsPortalOnboardingModal.show();

		this.updateUserRole({
			primaryRole: this.userRole
		});

		EventHandler.on(this.ActivatorsPortalOnboardingSplashNext, CLICK_EVENT, () => {
			this.fetchActivatorsPortalOnboardingSteps();
		});
		EventHandler.on(this.ActivatorsPortalOnboardingSuccessButton, CLICK_EVENT, () => {
			window.location.reload();
		});

		this.initEventHandlers();
	}

	initEventHandlers() {
		EventHandler.on(this.exitButton, CLICK_EVENT, () => {
			this.exitConfirmModal.show();
		});
		EventHandler.on(this.exitConfirmYes, CLICK_EVENT, () => {
			this.handleModalExit();
		});
		EventHandler.on(this.exitConfirmNo, CLICK_EVENT, () => {
			this.exitConfirmModal.hide();
		});

		if (this.dismissErrorButton) {
			EventHandler.on(this.dismissErrorButton, CLICK_EVENT, () => {
				this.hideError();
				window.location.reload();
			});
		}
	}

	showSuccessPage() {
		this.pushStepToDataLayer(6);
		dataLayer.push({
			event: 'activator_portal_onboarding',
			stepCompleted: 'Activator Portal onboarding submitted successfully'
		});
		this.ActivatorsPortalOnboardingStep.classList.add(HIDDEN_CLASS);
		this.ActivatorsPortalOnboardingSuccess.classList.remove(HIDDEN_CLASS);
	}

	showError(message) {
		if (this.errorText) {
			this.errorText.innerHTML = message;
		}
		if (this.errorContainer) {
			this.errorContainer.classList.remove(HIDDEN_CLASS);
		}
	}

	hideError() {
		if (this.errorContainer) {
			this.errorContainer.classList.add(HIDDEN_CLASS);
		}
	}

	initiateFormGenerator(stepsData) {
		this.ActivatorsPortalOnboardingSplash.classList.add(HIDDEN_CLASS);
		this.ActivatorsPortalOnboardingStep.classList.remove(HIDDEN_CLASS);

		this.formGenerator = new FormGenerator(this.ActivatorsPortalOnboardingQuestion, {
			questions: stepsData,
			totalSteps: 6,
			hasSeparator: true,
			formType: FormTypes.StepForm,
			themeColor: 'color-lta-blue',
			submitButton: {
				title: 'Confirm and submit'
			},
			radioAnswerField: 'id',
			isExitButtonEnabled: true,
			saveAddress: false,
			isBackButtonEnabled: true,
			exitButtonLabel: 'Close',
			exitButtonHandler: () => {
				this.exitConfirmModal.show();
			},
			backButtonHandlerFirstStep: () => {
				this.ActivatorsPortalOnboardingSplash.classList.remove(HIDDEN_CLASS);
				this.ActivatorsPortalOnboardingStep.classList.add(HIDDEN_CLASS);
			}
		});
		this.formGenerator.onChange((answers) => {
			this.prepareRequestObject(answers);
			this.saveActivatorsPortalOnboardingSteps(this.answerList);
		});

		this.formGenerator.onStepChange(() => {
			const currentStep = this.formGenerator._currentStep - 1;
			this.pushStepToDataLayer(currentStep);
		});

		this.exitButton.classList.add(HIDDEN_CLASS);

		this.pushStepToDataLayer(0);
	}

	pushStepToDataLayer(currentStep) {
		try {
			const stepMap = {
				0: 'Activator Portal onboarding started',
				1: 'Your Address',
				2: 'Venue Selection',
				3: 'Venue Details/Type',
				4: 'Your Experience',
				5: 'Your Sport',
				6: 'Your Audience'
			};

			const stepCompleted = stepMap[currentStep];

			dataLayer.push({
				event: 'activator_portal_onboarding',
				stepCompleted
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error.message);
		}
	}

	prepareRequestObject(stepsAnswers) {
		this.answerList = {};
		for (const stepKey in stepsAnswers) {
			const stepAnswersObjects = stepsAnswers[stepKey];
			for (const questionKey in stepAnswersObjects) {
				const answerObject = stepAnswersObjects[questionKey];
				const formatObject = {};
				formatObject[answerObject[KEY_OBJECT]] = answerObject[VALUE_OBJECT];
				if (answerObject[KEY_OBJECT] === 'reference') {
					formatObject[answerObject[KEY_OBJECT]] = answerObject.isOthers
						? answerObject.value
						: answerObject[REFERENCE_VALUE_OBJECT].trim();
				}
				this.answerList = {
					...this.answerList,
					...formatObject
				};
			}
		}
	}

	async updateUserRole(requestObject) {
		try {
			if (!this.updateRole) return;
			const url = this.updateRole;
			const response = await Request.post(url, requestObject, {
				local: IS_LOCAL,
				req: {
					apiMode: ApiMode.LtaError
				}
			});
			const { Success, success, error } = response;
			if (Success || success) {
				//console.log('Primary Role succesfully updated');
			}
			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			if (error.hasError) {
				//console.log('Error : ', error);
			}
		}
	}

	async fetchActivatorsPortalOnboardingSteps() {
		startLoadingAnimation();
		try {
			if (!this.formSteps) return;
			const url = this.formSteps;
			const response = await Request.get(url, {
				local: IS_LOCAL,
				req: {
					apiMode: ApiMode.LtaError
				}
			});
			const { Steps, error } = response;
			stopLoadingAnimation();
			if (Steps) {
				this.stepsData = Steps;
			}

			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			stopLoadingAnimation();
			if (error.hasError) {
				//console.log('Error : ', error);
			}
		}
	}

	async saveActivatorsPortalOnboardingSteps(requestObject) {
		startLoadingAnimation();
		try {
			if (!this.saveActivatorOnboardingDetails) return;
			const url = this.saveActivatorOnboardingDetails;
			const response = await Request.post(url, requestObject, {
				local: IS_LOCAL,
				req: {
					apiMode: ApiMode.LtaError
				}
			});
			const { Success, success, Response } = response;

			if (!(Success || success)) {
				stopLoadingAnimation();
				const errorMessage = Response || 'Failed to submit form';
				this.showError(errorMessage);
				return;
			}

			if (Success || success) {
				this.showSuccessPage();
			}
		} catch (error) {
			stopLoadingAnimation();
			const errorMessage = error?.message || error?.errorMessage || 'An unexpected error occurred';
			this.showError(errorMessage);
		} finally {
			stopLoadingAnimation();
		}
	}

	handleModalExit() {
		history.back();
	}
}

const globalActivatorsPortalOnboarding = new ActivatorsPortalOnboarding();
export default globalActivatorsPortalOnboarding;
